import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
} from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { useSchemes } from "../../../contexts/SchemesContext";
import { OffensivePlaybookPlay, OffensivePassProtectionConcept, OffensivePlayTag } from "../../../types/SchemesTypes";

export const OffensePlaybook = () => {
  const {
    isLoading,
    error,
    offensivePlaybookPlays,
  } = useSchemes();

  // State for all rows
  const [rows, setRows] = useState<OffensivePlaybookPlay[]>([]);

  // Effect for setting the rows
  useEffect(() => {
    if (offensivePlaybookPlays && offensivePlaybookPlays.length > 0) {
      const formattedRows = offensivePlaybookPlays.map((play, index) => ({
        ...play,
        id: index,
      }));
      setRows(formattedRows);
    }
  }, [offensivePlaybookPlays]);

  if (isLoading) {
    return <Typography>Loading offensive plays...</Typography>;
  }

  if (error) {
    return <Typography color="error">Error: {error}</Typography>;
  }

  // Define the columns for the DataGrid
  const columns: GridColDef[] = [
    {
      field: "personnel_formation",
      headerName: "Formation",
      width: 120,
    },
    {
      field: "code_word",
      headerName: "Code Word",
      width: 180,
    },
    {
      field: "call",
      headerName: "Call",
      width: 80,
    },
    {
      field: "name",
      headerName: "Name",
      width: 150,
    },
    {
      field: "pass_protection_concepts",
      headerName: "Protection",
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const concepts = params.row.pass_protection_concepts;
        if (!concepts || concepts.length === 0) return null;

        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {concepts.map((concept: OffensivePassProtectionConcept) => (
              <Chip
                key={concept.id}
                label={concept.name}
                size="small"
                sx={{
                  bgcolor: "rgba(63, 81, 181, 0.2)",
                  color: "white",
                  borderColor: "rgba(63, 81, 181, 0.5)",
                }}
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: "concept",
      headerName: "Concept",
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const concept = params.row.concept;
        return concept?.name || '';
      }
    },
    {
      field: "offensive_situations",
      headerName: "Situations",
      width: 250,
      renderCell: (params: GridRenderCellParams) => {
        const situations = params.row.offensive_situations;
        if (!situations || situations.length === 0) return null;

        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {situations.map((situation: string) => (
              <Chip
                key={situation}
                label={situation}
                size="small"
                sx={{
                  bgcolor: "rgba(63, 81, 181, 0.2)",
                  color: "white",
                  borderColor: "rgba(63, 81, 181, 0.5)",
                }}
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: "called_pass",
      headerName: "Type",
      width: 90,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? "Pass" : "Run"}
          size="small"
          sx={{
            bgcolor: params.value
              ? "rgba(46, 125, 50, 0.2)"
              : "rgba(211, 47, 47, 0.2)",
            color: params.value ? "#81c784" : "#e57373",
          }}
        />
      ),
    },
    {
      field: "tags",
      headerName: "Tags",
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const tags = params.row.tags;
        if (!tags || tags.length === 0) return null;

        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {tags.map((tag: OffensivePlayTag) => (
              <Chip
                key={tag.id}
                label={tag.name}
                size="small"
                sx={{
                  bgcolor: "rgba(123, 45, 58, 0.2)",
                  color: "white",
                  borderColor: "rgba(123, 45, 58, 0.5)",
                }}
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: "run_concept",
      headerName: "Run Concept",
      width: 130,
      renderCell: (params: GridRenderCellParams) => {
        const concept = params.row.run_concept;
        return concept?.name || '';
      }
    },
    {
      field: "pass_concept",
      headerName: "Pass Concept",
      width: 130,
      renderCell: (params: GridRenderCellParams) => {
        const concept = params.row.pass_concept;
        return concept?.name || '';
      }
    },
    {
      field: "strongside",
      headerName: "Strong",
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        // @ts-ignore
        const strongside = params.row.strongside;
        
        if (strongside === true) {
          return (
            <Chip
              label="Strong"
              size="small"
              sx={{
                bgcolor: "rgba(46, 125, 50, 0.2)",
                color: "#81c784",
              }}
            />
          );
        } else if (strongside === false) {
          return (
            <Chip
              label="Weak"
              size="small"
              sx={{
                bgcolor: "rgba(211, 47, 47, 0.2)",
                color: "#e57373",
              }}
            />
          );
        } else if (strongside === null) {
          return (
            <Chip
              label="Both"
              size="small"
              sx={{
                bgcolor: "rgba(25, 118, 210, 0.2)",
                color: "#64b5f6",
              }}
            />
          );
        }
        
        return null;
      }
    }
  ];

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <DataGridPro
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        density="compact"
        sx={{
          bgcolor: "#1e1e1e",
          color: "rgba(255, 255, 255, 0.8)",
          border: "none",
          height: "100%",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#2a2a2a",
            color: "white",
            fontWeight: "bold",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
          "& .MuiButton-root": {
            color: "rgba(255, 255, 255, 0.7)",
          },
          "& .MuiInputBase-root": {
            color: "rgba(255, 255, 255, 0.7)",
          },
          "& .MuiTablePagination-root": {
            color: "rgba(255, 255, 255, 0.7)",
          },
          "& .MuiIconButton-root": {
            color: "rgba(255, 255, 255, 0.7)",
          },
          "& .MuiDataGrid-toolbarContainer": {
            backgroundColor: "#1e1e1e",
            padding: "8px",
          },
        }}
      />
    </Box>
  );
};
